import React, { useRef, useState } from "react";
import { uploadDocument } from "../../lib/api";
import { Button } from "../ui/button";
import { UploadCloud, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function DocumentUpload({ document, onChange }) {
  const [uploading, setUploading] = useState(false);
  const docRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      toast.error("Please upload a PDF or DOCX file.");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadDocument(file);
      onChange({ key: res.key, name: res.name });
      toast.success("Governing document attached.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload the document.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="doc-upload-section" className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-center gap-2 text-foreground mb-1.5">
        <UploadCloud className="h-4 w-4 text-primary" />
        <p className="font-display font-600 text-sm">Attach a governing document <span className="text-muted-foreground font-normal">(optional)</span></p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        Optionally upload your governing document (election code, regulations, or by-laws) so the SmartBallot
        team can review the details of your voting event later. This is <strong>not required</strong> to
        generate your proposal — you can continue without it.
      </p>
      <input
        ref={docRef}
        data-testid="doc-file-input"
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        onChange={(e) => handleUpload(e.target.files?.[0])}
      />
      {document?.key ? (
        <div data-testid="doc-attached" className="flex items-center justify-between gap-3 rounded-lg bg-accent/50 px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium truncate">{document.name}</span>
          </div>
          <button
            type="button"
            data-testid="doc-remove-btn"
            onClick={() => onChange(null)}
            className="text-muted-foreground hover:text-destructive transition-colors"
            aria-label="Remove document"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="doc-upload-btn"
          onClick={() => !uploading && docRef.current?.click()}
          disabled={uploading}
          className="gap-2"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          {uploading ? "Uploading…" : "Choose file (PDF or DOCX)"}
        </Button>
      )}
    </div>
  );
}
